import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { formatString } from "../../../../../utils/constants/stringUtils";
import { convertToUserTimezone, DEFAULT_ADMIN_TIMEZONE } from "../../../../../utils/dateService";
import { TASK_EVENT_TYPE } from "../../../../../types/gamePass";
import { formatHistoryTimestamp } from "./monthlyChallengeHelpers";

dayjs.extend(customParseFormat);

const CALENDAR_DATE = "YYYY-MM-DD";
const DISPLAY_DATE = "DD MMMM YYYY";
const SHORT_DISPLAY_DATE = "D MMM YYYY";
const WINDOW_DISPLAY_DATE = "D MMM";
const EMPTY_CELL = "-";

export const LOGIN_TYPE_SIGNIN = "signin";
export const LOGIN_TYPE_GAME_PASS = "gamepass";
export const LOGIN_TYPE_ENROLLMENT = "enrollment";

export const LOGIN_ROW_VARIANT = {
  LOGIN: "login",
  RESTART: "restart",
  BREAK: "break",
  PENDING: "pending",
};

export const LOGIN_STATUS = {
  FIRST: "first",
  CONTINUED: "ok",
  RESTARTED: "restart",
  PENDING: "pending",
};

export const LOGIN_STREAK_DAY_STATUS = {
  FIRST_LOGIN: "first_login",
  CONTINUED: "continued",
  RESTARTED: "restarted",
  BROKEN: "broken",
};

/** Reset hour, login instants, and window labels use US Eastern (EST/EDT),
 *  not the admin picker's GMT offset (e.g. GMT+3). A 21:16 UTC login is
 *  5:16 PM EDT, not 00:16 the next calendar day. */
const WINDOW_BOUNDARY_DISPLAY = "D MMM, h:mm A z";
const LOGIN_OCCURRED_DISPLAY = "DD MMMM YYYY, HH:mm:ss";
const RESET_HOUR_DISPLAY = "h:mm A z";
const MS_DAY = 24 * 60 * 60 * 1000;
/** Close-call / live-window amber when remaining time is under 2h (FE-2666). */
export const LOGIN_WINDOW_CLOSE_CALL_MS = 2 * 60 * 60 * 1000;
export const LOGIN_WINDOW_URGENT_MS = LOGIN_WINDOW_CLOSE_CALL_MS;

export const LOGIN_STREAK_HISTORY_COLUMNS = [
  AppStrings.GAME_PASS_MONTHLY_COL_LOGIN_DATE_TIME,
  AppStrings.GAME_PASS_MONTHLY_COL_TYPE,
  AppStrings.GAME_PASS_MONTHLY_COL_COUNTS_TOWARD,
  AppStrings.GAME_PASS_MONTHLY_COL_CONSECUTIVE_DAYS,
  AppStrings.GAME_PASS_MONTHLY_COL_TIME_LEFT_IN_WINDOW,
  AppStrings.GAME_PASS_MONTHLY_COL_STATUS,
  AppStrings.GAME_PASS_MONTHLY_COL_STREAK_BROKEN,
  AppStrings.GAME_PASS_MONTHLY_COL_REWARD_HIT,
];

export const LOGIN_STREAK_HISTORY_SORT_KEYS = [
  "date",
  "type",
  "countsToward",
  "consecutiveDays",
  "timeLeft",
  "status",
  "streakBroken",
  "rewardHit",
];

export const LOGIN_STREAK_HISTORY_SEARCH_FILTERS = [
  {
    key: "date",
    label: AppStrings.GAME_PASS_MONTHLY_COL_LOGIN_DATE_TIME,
  },
];

const GAME_PASS_TYPE_VALUES = new Set([
  "game_pass",
  "gamepass",
  "opened_game_pass",
  "open_game_pass",
  "game_pass_open",
]);

const ENROLLMENT_TYPE_VALUES = new Set(["enrollment"]);

const isCalendarDate = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());

/** Calendar day. Bare `YYYY-MM-DD` values pass through so BE-authored
 *  login_days are not shifted. ISO instants use `timezone` when given. */
export const toCalendarDate = (value, timezone) => {
  if (value == null || value === "") return "";
  if (isCalendarDate(value)) return value.trim();
  const formatted = timezone
    ? convertToUserTimezone(value, CALENDAR_DATE, timezone)
    : convertToUserTimezone(value, CALENDAR_DATE);
  return formatted === "-" ? "" : formatted;
};

/** Calendar day on the login-streak clock (US Eastern). */
export const toChallengeCalendarDate = (value) =>
  toCalendarDate(value, DEFAULT_ADMIN_TIMEZONE);

export const nextCalendarDate = (yyyyMmDd) => {
  const parsed = dayjs(yyyyMmDd, CALENDAR_DATE, true);
  return parsed.isValid() ? parsed.add(1, "day").format(CALENDAR_DATE) : "";
};

const formatCalendarDate = (yyyyMmDd, format = DISPLAY_DATE) => {
  if (!yyyyMmDd) return "";
  const parsed = dayjs(yyyyMmDd, CALENDAR_DATE, true);
  return parsed.isValid() ? parsed.format(format) : yyyyMmDd;
};

export const formatConsecutiveStreak = (streak) => {
  const count = Number(streak);
  if (!Number.isFinite(count) || count <= 0) return EMPTY_CELL;
  return formatString(AppStrings.GAME_PASS_MONTHLY_DAYS_IN_A_ROW, {
    count: String(count),
  });
};

export const normalizeLoginType = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (GAME_PASS_TYPE_VALUES.has(normalized)) {
    return LOGIN_TYPE_GAME_PASS;
  }
  if (ENROLLMENT_TYPE_VALUES.has(normalized)) {
    return LOGIN_TYPE_ENROLLMENT;
  }
  return LOGIN_TYPE_SIGNIN;
};

export const loginTypeLabel = (type) => {
  if (type === LOGIN_TYPE_GAME_PASS) {
    return AppStrings.GAME_PASS_MONTHLY_LOGIN_TYPE_OPENED_GAME_PASS;
  }
  if (type === LOGIN_TYPE_ENROLLMENT) {
    return AppStrings.GAME_PASS_MONTHLY_LOGIN_TYPE_ENROLLED;
  }
  return AppStrings.GAME_PASS_MONTHLY_LOGIN_TYPE_LOGGED_IN;
};

export const loginStatusLabel = (status) => {
  if (status === LOGIN_STATUS.FIRST) {
    return AppStrings.GAME_PASS_MONTHLY_LOGIN_STATUS_FIRST;
  }
  if (status === LOGIN_STATUS.RESTARTED) {
    return AppStrings.GAME_PASS_MONTHLY_LOGIN_STATUS_RESTARTED;
  }
  if (status === LOGIN_STATUS.PENDING) {
    return AppStrings.GAME_PASS_MONTHLY_LOGIN_STATUS_WINDOW_OPEN;
  }
  return AppStrings.GAME_PASS_MONTHLY_LOGIN_STATUS_CONTINUED;
};

const asUtcResetHour = (value) => {
  if (typeof value === "number" && Number.isInteger(value)) {
    return value >= 0 && value <= 23 ? value : NaN;
  }
  if (typeof value === "string" && /^(?:[01]?\d|2[0-3])$/.test(value.trim())) {
    return Number(value.trim());
  }
  return NaN;
};

export const formatStreakResetHour = (utcHour, now = new Date()) => {
  const hour = asUtcResetHour(utcHour);
  if (!Number.isInteger(hour)) return "";
  const instant = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      hour,
      0,
      0,
    ),
  ).toISOString();
  const formatted = convertToUserTimezone(
    instant,
    RESET_HOUR_DISPLAY,
    DEFAULT_ADMIN_TIMEZONE,
  );
  return formatted && formatted !== "-" ? formatted : "";
};

export const getLoginResetTimeLabel = (...candidates) => {
  for (const candidate of candidates) {
    const fromHour = formatStreakResetHour(candidate);
    if (fromHour) return fromHour;
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  return "";
};

export const buildLoginStreakSubtitle = (resetTime) =>
  resetTime
    ? formatString(AppStrings.GAME_PASS_MONTHLY_DAILY_LOGIN_SUBTITLE, {
        resetTime,
      })
    : AppStrings.GAME_PASS_MONTHLY_DAILY_LOGIN_SUBTITLE_NO_TIME;

export const toEpochMs = (value) => {
  if (value == null || value === "") return NaN;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : NaN;
};

export const extractStreakHistoryDays = (payload) => {
  if (Array.isArray(payload?.days)) return payload.days;
  const nested = payload?.data;
  if (Array.isArray(nested?.days)) return nested.days;
  if (
    Array.isArray(nested) &&
    nested.some((entry) => entry?.window_end || entry?.status)
  ) {
    return nested;
  }
  return [];
};

/** UTC hour 0–23 from the streak-history envelope (`data.streak_reset_hour`). */
export const extractStreakResetHour = (payload) => {
  const nested = payload?.data;
  const candidate =
    payload?.streak_reset_hour ??
    (nested && !Array.isArray(nested) ? nested.streak_reset_hour : undefined);
  const hour = asUtcResetHour(candidate);
  return Number.isInteger(hour) ? hour : undefined;
};

const looksLikeStreakHistoryDays = (entries) =>
  Array.isArray(entries) &&
  entries.some(
    (entry) =>
      entry?.window_end != null ||
      entry?.window_start != null ||
      entry?.status != null ||
      entry?.consecutive_day != null ||
      entry?.occurred_at != null,
  );

export const formatLoginCountdown = (remainingMs) => {
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return "";
  const totalMinutes = Math.floor(remainingMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) {
    return formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_COUNTDOWN_MINUTES, {
      minutes: String(minutes),
    });
  }
  return formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_COUNTDOWN, {
    hours: String(hours),
    minutes: String(minutes),
  });
};

export const formatLoginOccurredAt = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string" && !value.trim()) return "";
  const formatted = convertToUserTimezone(
    value,
    LOGIN_OCCURRED_DISPLAY,
    DEFAULT_ADMIN_TIMEZONE,
  );
  if (!formatted || formatted === "-") return "";
  const zone = convertToUserTimezone(value, "z", DEFAULT_ADMIN_TIMEZONE);
  const zoneOk = Boolean(zone) && zone !== "-" && zone !== "z";
  return zoneOk ? `${formatted} ${zone}` : formatted;
};

export const formatWindowBoundary = (value) => {
  if (value == null || value === "") return "";
  const instant =
    typeof value === "number" ? new Date(toEpochMs(value)).toISOString() : value;
  const formatted = convertToUserTimezone(
    instant,
    WINDOW_BOUNDARY_DISPLAY,
    DEFAULT_ADMIN_TIMEZONE,
  );
  if (formatted && formatted !== "-") return formatted;
  return formatHistoryTimestamp(instant);
};

const emptyTimeLeft = (overrides = {}) => ({
  amount: EMPTY_CELL,
  sub: "",
  closeCall: false,
  live: false,
  expired: false,
  sortMs: 0,
  windowEnd: undefined,
  ...overrides,
});

/**
 * Table Time Left cell. Completed rows use remaining time at login
 * (`window_end - occurred_at`). The open pending row uses `window_end - now`.
 */
export const formatTimeLeftInWindow = ({
  windowEnd,
  occurredAt,
  now = Date.now(),
  pending = false,
} = {}) => {
  const endMs = toEpochMs(windowEnd);
  const closeLabel = formatWindowBoundary(windowEnd);
  if (!Number.isFinite(endMs)) {
    return emptyTimeLeft();
  }

  if (pending) {
    const remaining = endMs - now;
    if (remaining <= 0) {
      return emptyTimeLeft({
        sub: closeLabel
          ? formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_WINDOW_CLOSED, {
              date: closeLabel,
            })
          : "",
        live: true,
        expired: true,
        windowEnd,
      });
    }
    const countdown = formatLoginCountdown(remaining);
    return {
      amount: countdown
        ? formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_EXPIRES_IN, {
            countdown,
          })
        : EMPTY_CELL,
      sub: closeLabel
        ? formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_WINDOW_CLOSES, {
            date: closeLabel,
          })
        : "",
      closeCall: remaining <= LOGIN_WINDOW_CLOSE_CALL_MS,
      live: true,
      expired: false,
      sortMs: remaining,
      windowEnd,
    };
  }

  const loginMs = toEpochMs(occurredAt);
  if (!Number.isFinite(loginMs)) {
    return emptyTimeLeft({
      sub: closeLabel
        ? formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_WINDOW_CLOSED, {
            date: closeLabel,
          })
        : "",
    });
  }

  const remainingAtLogin = Math.max(0, endMs - loginMs);
  const countdown = formatLoginCountdown(remainingAtLogin);
  const closeCall =
    remainingAtLogin > 0 && remainingAtLogin <= LOGIN_WINDOW_CLOSE_CALL_MS;
  const closedSub = closeLabel
    ? formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_WINDOW_CLOSED, {
        date: closeLabel,
      })
    : "";
  return {
    amount: countdown
      ? formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_TIME_LEFT, {
          countdown,
        })
      : EMPTY_CELL,
    sub: closeCall
      ? [AppStrings.GAME_PASS_MONTHLY_LOGIN_CUT_IT_CLOSE, closedSub]
          .filter(Boolean)
          .join(" · ")
      : closedSub,
    closeCall,
    live: false,
    expired: false,
    sortMs: remainingAtLogin,
    windowEnd,
  };
};

export const resolveRolledWindow = (windowStart, windowEnd, now = Date.now()) => {
  const endMs = toEpochMs(windowEnd);
  if (!Number.isFinite(endMs)) return null;
  const startMs = toEpochMs(windowStart);
  const duration =
    Number.isFinite(startMs) && endMs > startMs ? endMs - startMs : MS_DAY;
  if (duration <= 0) return null;
  const currentStart = Number.isFinite(startMs) ? startMs : endMs - duration;
  if (now < endMs) {
    return {
      windowStartMs: currentStart,
      windowEndMs: endMs,
      rolled: false,
    };
  }
  const periods = Math.floor((now - endMs) / duration) + 1;
  const windowEndMs = endMs + periods * duration;
  return {
    windowStartMs: windowEndMs - duration,
    windowEndMs,
    rolled: true,
  };
};

export const findLiveLoginWindow = (days = [], now = Date.now()) => {
  const containing = days.find((day) => {
    const start = toEpochMs(day.window_start ?? day.windowStart);
    const end = toEpochMs(day.window_end ?? day.windowEnd);
    return (
      Number.isFinite(start) &&
      Number.isFinite(end) &&
      now >= start &&
      now < end
    );
  });
  if (containing) {
    return {
      windowStart: containing.window_start ?? containing.windowStart,
      windowEnd: containing.window_end ?? containing.windowEnd,
      loggedInThisWindow: Boolean(
        containing.occurred_at ?? containing.occurredAt,
      ),
    };
  }

  let latest = null;
  days.forEach((day) => {
    const end = toEpochMs(day.window_end ?? day.windowEnd);
    if (!Number.isFinite(end)) return;
    if (!latest || end > latest.end) {
      latest = {
        end,
        day,
        windowStart: day.window_start ?? day.windowStart,
        windowEnd: day.window_end ?? day.windowEnd,
      };
    }
  });
  if (!latest) return null;

  const rolled = resolveRolledWindow(latest.windowStart, latest.windowEnd, now);
  if (!rolled) return null;
  const stillInLatestWindow =
    !rolled.rolled && now >= rolled.windowStartMs && now < rolled.windowEndMs;
  const previousHadLogin = Boolean(
    latest.day?.occurred_at ?? latest.day?.occurredAt,
  );
  return {
    windowStart: new Date(rolled.windowStartMs).toISOString(),
    windowEnd: new Date(rolled.windowEndMs).toISOString(),
    loggedInThisWindow: stillInLatestWindow && previousHadLogin,
    rolled: rolled.rolled,
    previousWindowStart: latest.windowStart,
    previousWindowEnd: latest.windowEnd,
    previousHadLogin,
  };
};

export const resolveLiveLoginWindow = ({
  days = [],
  progressTask,
  now = Date.now(),
} = {}) => {
  const fromDays = findLiveLoginWindow(days, now);
  if (fromDays) return fromDays;
  const windowEnd =
    progressTask?.window_ends_at ?? progressTask?.window_closes_at;
  const endMs = toEpochMs(windowEnd);
  if (!Number.isFinite(endMs)) return null;
  const startMs = endMs - MS_DAY;
  const rolled = resolveRolledWindow(
    new Date(startMs).toISOString(),
    typeof windowEnd === "number" ? new Date(endMs).toISOString() : windowEnd,
    now,
  );
  if (!rolled) return null;
  const lastLogin = toEpochMs(progressTask?.last_login_at);
  const stillInWindow =
    !rolled.rolled && now >= rolled.windowStartMs && now < rolled.windowEndMs;
  return {
    windowStart: new Date(rolled.windowStartMs).toISOString(),
    windowEnd: new Date(rolled.windowEndMs).toISOString(),
    loggedInThisWindow:
      stillInWindow &&
      Number.isFinite(lastLogin) &&
      lastLogin >= rolled.windowStartMs &&
      lastLogin < rolled.windowEndMs,
    rolled: rolled.rolled,
  };
};

const sameWindow = (left, right) => {
  const leftStart = toEpochMs(left?.window_start ?? left?.windowStart);
  const leftEnd = toEpochMs(left?.window_end ?? left?.windowEnd);
  const rightStart = toEpochMs(right?.windowStart ?? right?.window_start);
  const rightEnd = toEpochMs(right?.windowEnd ?? right?.window_end);
  return (
    Number.isFinite(leftStart) &&
    Number.isFinite(leftEnd) &&
    leftStart === rightStart &&
    leftEnd === rightEnd
  );
};

export const buildTimeLeftTile = ({
  windowStart,
  windowEnd,
  loggedInThisWindow = false,
  now = Date.now(),
} = {}) => {
  const rolled = resolveRolledWindow(windowStart, windowEnd, now);
  if (!rolled) {
    return {
      value: "",
      note: "",
      tone: "",
      windowStart,
      windowEnd,
      loggedInThisWindow: false,
    };
  }
  const remaining = rolled.windowEndMs - now;
  const countdown = formatLoginCountdown(remaining);
  const closesAt = formatWindowBoundary(rolled.windowEndMs);
  const isLoggedIn = rolled.rolled ? false : Boolean(loggedInThisWindow);
  if (!countdown) {
    return {
      value: "",
      note: "",
      tone: "",
      windowStart,
      windowEnd: new Date(rolled.windowEndMs).toISOString(),
      loggedInThisWindow: isLoggedIn,
    };
  }
  if (isLoggedIn) {
    return {
      value: formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_LOGGED_IN_LEFT, {
        countdown,
      }),
      note: closesAt
        ? formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_WINDOW_CLOSES, {
            date: closesAt,
          })
        : "",
      tone: "success",
      windowStart,
      windowEnd: new Date(rolled.windowEndMs).toISOString(),
      loggedInThisWindow: true,
    };
  }
  const urgent = remaining <= LOGIN_WINDOW_URGENT_MS;
  return {
    value: countdown,
    note: [
      AppStrings.GAME_PASS_MONTHLY_LOGIN_NOT_LOGGED_IN_YET,
      closesAt
        ? formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_WINDOW_CLOSES, {
            date: closesAt,
          })
        : "",
    ]
      .filter(Boolean)
      .join(" · "),
    tone: urgent ? "danger" : "window",
    windowStart,
    windowEnd: new Date(rolled.windowEndMs).toISOString(),
    loggedInThisWindow: false,
  };
};

/**
 * Walk unique login days in chronological order and stamp a running streak.
 * A gap of more than one calendar day resets the streak to 1 and records the
 * first missed day as `streakBrokenOn` on the login that restarts it.
 */
export const annotateLoginStreaks = (days) => {
  const sorted = [...days].sort((first, second) =>
    String(first.date).localeCompare(String(second.date)),
  );
  let streak = 0;
  let previousDate = null;
  return sorted.map((day) => {
    const isConsecutive =
      Boolean(previousDate) && nextCalendarDate(previousDate) === day.date;
    const streakBrokenOn =
      !isConsecutive && previousDate ? nextCalendarDate(previousDate) : null;
    streak = isConsecutive ? streak + 1 : 1;
    previousDate = day.date;
    return { ...day, streak, streakBrokenOn };
  });
};

const readLoginType = (entry) =>
  normalizeLoginType(
    entry?.login_type ??
      entry?.type ??
      entry?.source ??
      entry?.credited_by,
  );

const readLoginDay = (entry, fallbackUsername) => {
  const date = toCalendarDate(
    entry?.date ?? entry?.first_login_at ?? entry?.occurred_at,
  );
  if (!date) return null;
  const consecutive = entry?.consecutive_day ?? entry?.streak;
  return {
    date,
    firstLoginAt: entry?.occurred_at ?? entry?.first_login_at ?? entry?.date,
    occurredAt: entry?.occurred_at ?? entry?.first_login_at,
    username: entry?.username || fallbackUsername,
    streak: consecutive,
    streakBrokenOn: entry?.streak_broken_on,
    loginType: readLoginType(entry),
    countsToward:
      entry?.counts_toward ?? entry?.window_label ?? entry?.window_date,
    timeLeftInWindow:
      entry?.time_left_in_window ?? entry?.window_time_remaining,
    windowStart: entry?.window_start,
    windowEnd: entry?.window_end ?? entry?.window_ends_at,
    windowClosedAt:
      entry?.window_end ?? entry?.window_closed_at ?? entry?.window_ends_at,
    status: entry?.status,
    rewardHit: entry?.reward_hit ?? entry?.reward_label,
  };
};

const uniqueFirstLoginPerDay = (events, fallbackUsername) => {
  const byDate = new Map();
  [...events]
    .sort((first, second) => {
      const firstTime = new Date(first?.occurred_at ?? 0).getTime();
      const secondTime = new Date(second?.occurred_at ?? 0).getTime();
      return firstTime - secondTime;
    })
    .forEach((event) => {
      const payload = event?.payload || {};
      const occurredAt = event?.occurred_at ?? payload.first_login_at;
      const date = toCalendarDate(payload.date ?? occurredAt);
      if (!date || byDate.has(date)) return;
      byDate.set(date, {
        date,
        firstLoginAt: occurredAt,
        occurredAt,
        username: payload.username || event?.username || fallbackUsername,
        loginType: readLoginType(payload),
        countsToward: payload.counts_toward ?? payload.window_label,
        timeLeftInWindow:
          payload.time_left_in_window ?? payload.window_time_remaining,
        windowStart: payload.window_start,
        windowEnd: payload.window_end ?? payload.window_ends_at,
        windowClosedAt:
          payload.window_end ??
          payload.window_closed_at ??
          payload.window_ends_at,
        rewardHit: payload.reward_hit ?? payload.reward_label,
      });
    });
  return [...byDate.values()];
};

/** SOCIAL history is mixed (follow / referral / login). Only typed
 *  `daily_login` rows count — untyped records must not become login days. */
const getDailyLoginEvents = (socialEvents) =>
  socialEvents.filter(
    (event) => event.event_type === TASK_EVENT_TYPE.DAILY_LOGIN,
  );

/** Instant at `streak_reset_hour` UTC on a BE window key (`YYYY-MM-DD`). */
const utcWindowStartIso = (calendarDate, streakResetHour) => {
  if (!isCalendarDate(calendarDate)) return "";
  const hour = asUtcResetHour(streakResetHour);
  if (!Number.isInteger(hour)) return "";
  const [year, month, dayOfMonth] = calendarDate.split("-").map(Number);
  return new Date(
    Date.UTC(year, month - 1, dayOfMonth, hour, 0, 0),
  ).toISOString();
};

/**
 * Local calendar day the challenge window belongs to on the US Eastern clock
 * (same zone as reset hour and window-close labels).
 */
export const resolveWindowCalendarDate = (day, streakResetHour) => {
  if (!day) return "";
  const fromStart = toChallengeCalendarDate(day.windowStart);
  if (fromStart) return fromStart;
  const fromResetHour = toChallengeCalendarDate(
    utcWindowStartIso(day.date, streakResetHour),
  );
  if (fromResetHour) return fromResetHour;
  return toCalendarDate(day.date);
};

const resolveCountsToward = (day, streakResetHour) => {
  if (typeof day.countsToward === "string" && day.countsToward.trim()) {
    return day.countsToward.trim();
  }
  const windowDate =
    resolveWindowCalendarDate(day, streakResetHour) ||
    toCalendarDate(day.countsToward) ||
    day.date;
  const shortDate = formatCalendarDate(windowDate, WINDOW_DISPLAY_DATE);
  return shortDate
    ? formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_COUNTS_TOWARD, {
        date: shortDate,
      })
    : EMPTY_CELL;
};

const resolveStatus = (day, isFirstLoginOfMonth) => {
  if (day.status === LOGIN_STREAK_DAY_STATUS.FIRST_LOGIN) {
    return LOGIN_STATUS.FIRST;
  }
  if (day.status === LOGIN_STREAK_DAY_STATUS.RESTARTED || day.streakBrokenOn) {
    return LOGIN_STATUS.RESTARTED;
  }
  if (day.status === LOGIN_STREAK_DAY_STATUS.CONTINUED) {
    return LOGIN_STATUS.CONTINUED;
  }
  if (isFirstLoginOfMonth) return LOGIN_STATUS.FIRST;
  return LOGIN_STATUS.CONTINUED;
};

const buildBreakRow = (brokenOn) => {
  const formatted = formatCalendarDate(brokenOn);
  const label = formatString(
    AppStrings.GAME_PASS_MONTHLY_LOGIN_STREAK_BROKEN_ROW,
    { date: formatted || brokenOn },
  );
  return {
    id: `break-${brokenOn}`,
    variant: LOGIN_ROW_VARIANT.BREAK,
    fullWidth: true,
    excludeFromCount: true,
    searchValues: { date: formatted || brokenOn },
    sortValues: { date: brokenOn, streakBroken: brokenOn },
    presentation: {
      breakLabel: label,
    },
    cells: [label],
  };
};

const buildHistoryRow = (
  day,
  { isFirstLoginOfMonth, now = Date.now(), streakResetHour } = {},
) => {
  const loginDate =
    formatLoginOccurredAt(day.firstLoginAt || day.occurredAt) ||
    formatCalendarDate(day.date) ||
    EMPTY_CELL;
  const type = day.loginType || LOGIN_TYPE_SIGNIN;
  const typeLabel = loginTypeLabel(type);
  const countsToward = resolveCountsToward(day, streakResetHour);
  const consecutiveDays = Number(day.streak);
  const consecutiveLabel =
    Number.isFinite(consecutiveDays) && consecutiveDays > 0
      ? String(consecutiveDays)
      : EMPTY_CELL;
  const status = resolveStatus(day, isFirstLoginOfMonth);
  const statusLabel = loginStatusLabel(status);
  const brokenFormatted = day.streakBrokenOn
    ? formatCalendarDate(day.streakBrokenOn)
    : "";
  const brokenOn = brokenFormatted
    ? formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_MISSED, {
        date: brokenFormatted,
      })
    : EMPTY_CELL;
  const windowEnd = day.windowEnd || day.windowClosedAt;
  const occurredAt = day.occurredAt || day.firstLoginAt;
  const timeLeftModel = formatTimeLeftInWindow({
    windowEnd,
    occurredAt,
    now,
    pending: false,
  });
  const timeLeft =
    timeLeftModel.amount !== EMPTY_CELL
      ? timeLeftModel.amount
      : day.timeLeftInWindow || EMPTY_CELL;
  const timeLeftSub = timeLeftModel.sub;
  const rewardHit = day.rewardHit || EMPTY_CELL;
  const isRestart =
    day.status === LOGIN_STREAK_DAY_STATUS.RESTARTED ||
    Boolean(day.streakBrokenOn);
  const variant = isRestart ? LOGIN_ROW_VARIANT.RESTART : LOGIN_ROW_VARIANT.LOGIN;

  return {
    id: day.date,
    variant,
    searchValues: { date: loginDate },
    sortValues: {
      date: day.date,
      type: typeLabel,
      countsToward,
      consecutiveDays: Number.isFinite(consecutiveDays) ? consecutiveDays : 0,
      timeLeft: timeLeftModel.sortMs,
      status: statusLabel,
      streakBroken: day.streakBrokenOn || "",
      rewardHit,
    },
    presentation: {
      type,
      consecutiveDays: consecutiveLabel,
      consecutiveReset: isRestart,
      status,
      timeLeftAmount: timeLeft,
      timeLeftSub,
      timeLeftCloseCall: timeLeftModel.closeCall,
      timeLeftLive: false,
      windowEnd: timeLeftModel.windowEnd,
      streakBroken: brokenOn,
      rewardHit,
    },
    cells: [
      loginDate,
      typeLabel,
      countsToward,
      consecutiveLabel,
      timeLeft,
      statusLabel,
      brokenOn,
      rewardHit,
    ],
  };
};

const buildPendingWindowRow = (live, now) => {
  const timeLeftModel = formatTimeLeftInWindow({
    windowEnd: live.windowEnd,
    now,
    pending: true,
  });
  const windowDate =
    toChallengeCalendarDate(live.windowStart) ||
    toChallengeCalendarDate(live.windowEnd) ||
    "live";
  const countsToward = resolveCountsToward({
    windowStart: live.windowStart,
    date: windowDate,
  });
  const status = LOGIN_STATUS.PENDING;
  const statusLabel = loginStatusLabel(status);
  const loginLabel = AppStrings.GAME_PASS_MONTHLY_LOGIN_NOT_LOGGED_IN_YET;
  return {
    id: `pending-${windowDate}`,
    variant: LOGIN_ROW_VARIANT.PENDING,
    excludeFromCount: true,
    searchValues: { date: loginLabel },
    sortValues: {
      date: windowDate,
      type: "",
      countsToward,
      consecutiveDays: 0,
      timeLeft: timeLeftModel.sortMs,
      status: statusLabel,
      streakBroken: "",
      rewardHit: EMPTY_CELL,
    },
    presentation: {
      consecutiveDays: EMPTY_CELL,
      status,
      timeLeftAmount: timeLeftModel.amount,
      timeLeftSub: timeLeftModel.sub,
      timeLeftCloseCall: timeLeftModel.closeCall,
      timeLeftLive: true,
      windowEnd: live.windowEnd,
      windowStart: live.windowStart,
      streakBroken: EMPTY_CELL,
      rewardHit: EMPTY_CELL,
    },
    cells: [
      loginLabel,
      EMPTY_CELL,
      countsToward,
      EMPTY_CELL,
      timeLeftModel.amount,
      statusLabel,
      EMPTY_CELL,
      EMPTY_CELL,
    ],
  };
};

const appendLiveWindowRows = (rows, { days = [], progressTask, now }) => {
  const live = resolveLiveLoginWindow({ days, progressTask, now });
  if (!live) return rows;

  let nextRows = rows;
  const duration =
    toEpochMs(live.windowEnd) - toEpochMs(live.windowStart);
  const liveStart = toEpochMs(live.windowStart);
  let cursor = Number.NaN;
  if (live.rolled && Number.isFinite(duration) && duration > 0) {
    if (live.previousHadLogin === false) {
      cursor = toEpochMs(live.previousWindowStart);
    } else {
      cursor = toEpochMs(live.previousWindowEnd);
    }
  }
  let safety = 0;
  while (
    Number.isFinite(cursor) &&
    Number.isFinite(liveStart) &&
    cursor < liveStart &&
    safety < 62
  ) {
    safety += 1;
    const missed = {
      windowStart: new Date(cursor).toISOString(),
      windowEnd: new Date(cursor + duration).toISOString(),
    };
    if (!days.some((day) => sameWindow(day, missed))) {
      const brokenOn = toChallengeCalendarDate(missed.windowStart);
      if (
        brokenOn &&
        !nextRows.some((row) => row.id === `break-${brokenOn}`)
      ) {
        nextRows = [...nextRows, buildBreakRow(brokenOn)];
      }
    }
    cursor += duration;
  }

  if (live.loggedInThisWindow) return nextRows;
  if (days.some((day) => sameWindow(day, live))) return nextRows;

  const pending = buildPendingWindowRow(live, now);
  if (pending.presentation.timeLeftAmount === EMPTY_CELL) return nextRows;
  return [...nextRows, pending];
};

const readStreakHistoryDays = (entries, fallbackUsername) =>
  entries.map((entry) => readLoginDay(entry, fallbackUsername)).filter(Boolean);

const resolveAuthoredStreakDays = ({
  streakDays,
  progressTask,
  fallbackUsername,
}) => {
  if (Array.isArray(streakDays) && streakDays.length > 0) {
    return readStreakHistoryDays(streakDays, fallbackUsername);
  }
  const progressDays = extractStreakHistoryDays(progressTask);
  if (progressDays.length > 0) {
    return readStreakHistoryDays(progressDays, fallbackUsername);
  }
  if (looksLikeStreakHistoryDays(progressTask?.login_days)) {
    return readStreakHistoryDays(progressTask.login_days, fallbackUsername);
  }
  return [];
};

const buildRowsFromStreakDays = (days, now, streakResetHour) => {
  const rows = [];
  let previousBrokenDate = null;
  days.forEach((day, index) => {
    if (day.status === LOGIN_STREAK_DAY_STATUS.BROKEN) {
      previousBrokenDate = resolveWindowCalendarDate(day, streakResetHour);
      rows.push(buildBreakRow(previousBrokenDate));
      return;
    }
    const withBreak =
      day.status === LOGIN_STREAK_DAY_STATUS.RESTARTED && previousBrokenDate
        ? { ...day, streakBrokenOn: previousBrokenDate }
        : day;
    rows.push(
      buildHistoryRow(withBreak, {
        isFirstLoginOfMonth:
          index === 0 || day.status === LOGIN_STREAK_DAY_STATUS.FIRST_LOGIN,
        now,
        streakResetHour,
      }),
    );
  });
  return rows;
};

/**
 * Login History rows for a monthly Login Streak challenge (FE-2647 / FE-2664).
 *
 * Prefers forensic `days: []` (streak-history or inlined on progress).
 * Falls back to `progressTask.login_days`, then `daily_login` social events.
 */
export const buildLoginStreakHistoryRows = ({
  progressTask,
  socialEvents = [],
  fallbackUsername,
  streakDays,
  streakResetHour,
  now = Date.now(),
} = {}) => {
  const forensicDays = resolveAuthoredStreakDays({
    streakDays,
    progressTask,
    fallbackUsername,
  });
  const resolvedHour =
    streakResetHour ?? extractStreakResetHour(progressTask);
  if (forensicDays.length > 0) {
    return appendLiveWindowRows(
      buildRowsFromStreakDays(forensicDays, now, resolvedHour),
      {
        days: forensicDays,
        progressTask,
        now,
      },
    );
  }

  const authoredDays = Array.isArray(progressTask?.login_days)
    ? progressTask.login_days
        .map((entry) => readLoginDay(entry, fallbackUsername))
        .filter(Boolean)
    : [];
  const sourceDays =
    authoredDays.length > 0
      ? authoredDays
      : uniqueFirstLoginPerDay(
          getDailyLoginEvents(socialEvents),
          fallbackUsername,
        );
  const needsStreaks = sourceDays.some(
    (day) => !Number.isFinite(Number(day.streak)) || Number(day.streak) <= 0,
  );
  const days = needsStreaks ? annotateLoginStreaks(sourceDays) : sourceDays;
  const rows = [];
  days.forEach((day, index) => {
    if (day.streakBrokenOn) {
      rows.push(buildBreakRow(day.streakBrokenOn));
    }
    rows.push(
      buildHistoryRow(day, {
        isFirstLoginOfMonth: index === 0,
        now,
        streakResetHour: resolvedHour,
      }),
    );
  });
  return appendLiveWindowRows(rows, { days, progressTask, now });
};

const formatShortDate = (value) => {
  const calendar = toCalendarDate(value);
  if (calendar) {
    return formatCalendarDate(calendar, SHORT_DISPLAY_DATE);
  }
  const timestamp = formatHistoryTimestamp(value);
  return timestamp || "";
};

const creditedStreakDays = (days) =>
  days.filter(
    (day) =>
      day.status !== LOGIN_STREAK_DAY_STATUS.BROKEN &&
      Boolean(day.occurredAt || day.firstLoginAt),
  );

const currentStreakFromDays = (days) => {
  const lastBreakIndex = [...days]
    .map((day, index) =>
      day.status === LOGIN_STREAK_DAY_STATUS.BROKEN ? index : -1,
    )
    .reduce((latest, index) => (index > latest ? index : latest), -1);
  const current = days
    .slice(lastBreakIndex + 1)
    .filter(
      (day) =>
        day.status !== LOGIN_STREAK_DAY_STATUS.BROKEN &&
        Boolean(day.occurredAt || day.firstLoginAt),
    );
  if (current.length === 0) {
    return { count: 0, since: "" };
  }
  const last = current[current.length - 1];
  const count = Number(last.streak);
  return {
    count: Number.isFinite(count) && count > 0 ? count : current.length,
    since: current[0].date,
  };
};

/**
 * Summary-tile values for the Daily Login Challenge card (FE-2664).
 * Forensic `days` are the source of truth when present; otherwise progress
 * fields fill what they can and empty tiles stay "—".
 * Streaks Broken is `days[].status === "broken"` or progress `streaks_broken`
 * — never inferred from social-event history rows.
 */
export const buildLoginStreakSummary = ({
  progressTask,
  historyRows = [],
  streakDays,
  streakResetHour,
  now = Date.now(),
} = {}) => {
  const forensicDays = resolveAuthoredStreakDays({
    streakDays,
    progressTask,
  });
  const loginRows = historyRows.filter(
    (row) =>
      row?.variant !== LOGIN_ROW_VARIANT.BREAK &&
      row?.variant !== LOGIN_ROW_VARIANT.PENDING,
  );
  const firstLoginRow = loginRows[0];
  const lastLoginRow = loginRows[loginRows.length - 1];

  const credited = creditedStreakDays(forensicDays);
  const hasLogins = credited.length > 0 || loginRows.length > 0;
  const firstCredited = credited[0];
  const firstLoginAt =
    formatLoginOccurredAt(
      firstCredited?.occurredAt ||
        firstCredited?.firstLoginAt ||
        progressTask?.first_login_at,
    ) ||
    firstLoginRow?.cells?.[0] ||
    "";

  const streakFromDays = currentStreakFromDays(forensicDays);
  const streakCount =
    streakFromDays.count ||
    Number(progressTask?.streak_count) ||
    Number(lastLoginRow?.sortValues?.consecutiveDays) ||
    0;
  const streakSince =
    formatShortDate(streakFromDays.since) ||
    (forensicDays.length === 0
      ? formatShortDate(lastLoginRow?.sortValues?.date)
      : "");

  const brokenDays = forensicDays.filter(
    (day) => day.status === LOGIN_STREAK_DAY_STATUS.BROKEN,
  );
  const breaksCount =
    brokenDays.length || Number(progressTask?.streaks_broken) || 0;
  const resolvedHour =
    streakResetHour ?? extractStreakResetHour(progressTask);
  const lastBreak =
    formatShortDate(
      resolveWindowCalendarDate(
        brokenDays[brokenDays.length - 1],
        resolvedHour,
      ),
    ) || formatShortDate(progressTask?.streak_broken_on);

  const liveWindow = resolveLiveLoginWindow({
    days: forensicDays,
    progressTask,
    now,
  });
  const timeLeftTile = liveWindow
    ? buildTimeLeftTile({
        windowStart: liveWindow.windowStart,
        windowEnd: liveWindow.windowEnd,
        loggedInThisWindow: liveWindow.loggedInThisWindow,
        now,
      })
    : {
        value:
          progressTask?.window_time_left ||
          progressTask?.time_left_in_window ||
          "",
        note: "",
        tone: "",
        windowStart: undefined,
        windowEnd: undefined,
        loggedInThisWindow: false,
      };

  if (!timeLeftTile.note && timeLeftTile.value && !liveWindow) {
    const windowClosesAt =
      formatWindowBoundary(
        progressTask?.window_ends_at ?? progressTask?.window_closes_at,
      ) ||
      formatHistoryTimestamp(
        progressTask?.window_ends_at ?? progressTask?.window_closes_at,
      ) ||
      formatShortDate(
        progressTask?.window_ends_at ?? progressTask?.window_closes_at,
      );
    timeLeftTile.note = [
      AppStrings.GAME_PASS_MONTHLY_LOGIN_NOT_LOGGED_IN_YET,
      windowClosesAt
        ? formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_WINDOW_CLOSES, {
            date: windowClosesAt,
          })
        : "",
    ]
      .filter(Boolean)
      .join(" · ");
    timeLeftTile.tone = timeLeftTile.value ? "window" : "";
  }

  if (!hasLogins) {
    return {
      timeLeft: liveWindow ? timeLeftTile : { value: "", note: "", tone: "" },
      currentStreak: { value: "", note: "", tone: "" },
      firstLogin: { value: "", note: "", tone: "" },
      streaksBroken: { value: "", note: "", tone: "" },
    };
  }

  return {
    timeLeft: timeLeftTile,
    currentStreak: {
      value:
        streakCount > 0
          ? formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_DAYS, {
              count: String(streakCount),
            })
          : "",
      note: streakSince
        ? formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_SINCE, {
            date: streakSince,
          })
        : "",
      tone: streakCount > 0 ? "success" : "",
    },
    firstLogin: {
      value: firstLoginAt && firstLoginAt !== EMPTY_CELL ? firstLoginAt : "",
      note: firstLoginAt
        ? AppStrings.GAME_PASS_MONTHLY_LOGIN_STREAK_CLOCK_STARTED
        : "",
      tone: "",
    },
    streaksBroken: {
      value: String(breaksCount),
      note:
        breaksCount > 0 && lastBreak
          ? formatString(AppStrings.GAME_PASS_MONTHLY_LOGIN_LAST_BREAK, {
              date: lastBreak,
            })
          : "",
      tone: breaksCount > 0 ? "danger" : "",
    },
  };
};

/**
 * Reward Hit is the first time a live run crosses a tier threshold.
 * Later runs that only match `consecutive_day` after a break must not
 * re-award rungs already unlocked by the high-water mark.
 */
export const resolveLoginRewardHits = (rows = [], milestones = []) => {
  let bestSoFar = 0;
  const thresholds = milestones
    .map((milestone) => ({
      threshold: Number(milestone?.threshold),
      rewardLabel: milestone?.rewardLabel,
    }))
    .filter(
      (milestone) =>
        Number.isFinite(milestone.threshold) && milestone.threshold > 0,
    );

  return rows.map((row) => {
    if (
      row?.variant === LOGIN_ROW_VARIANT.BREAK ||
      row?.variant === LOGIN_ROW_VARIANT.PENDING
    ) {
      return row;
    }
    const consecutiveDays = Number(row?.presentation?.consecutiveDays);
    const authored = row?.presentation?.rewardHit;
    let rewardHit =
      authored && authored !== EMPTY_CELL ? authored : EMPTY_CELL;
    if (Number.isFinite(consecutiveDays) && consecutiveDays > bestSoFar) {
      if (rewardHit === EMPTY_CELL) {
        const newlyReached = thresholds.filter(
          (tier) =>
            tier.threshold > bestSoFar &&
            tier.threshold <= consecutiveDays &&
            tier.rewardLabel,
        );
        if (newlyReached.length > 0) {
          rewardHit = newlyReached
            .map((tier) => tier.rewardLabel)
            .join(", ");
        }
      }
      bestSoFar = consecutiveDays;
    }
    const cells = Array.isArray(row.cells) ? [...row.cells] : row.cells;
    if (Array.isArray(cells) && cells.length > 7) {
      cells[7] = rewardHit;
    }
    return {
      ...row,
      presentation: { ...row.presentation, rewardHit },
      sortValues: { ...row.sortValues, rewardHit },
      cells,
    };
  });
};
